import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, FileText, Search, Filter, Eye } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Pagination } from '../components/shared/Pagination';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomersPage = () => {
  const { hasPermission } = useAuth();
  const canDelete = hasPermission('delete_customer');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewCustomerName, setPreviewCustomerName] = useState('');
  const [previewCustomerId, setPreviewCustomerId] = useState('');
  const [statementFilters, setStatementFilters] = useState({
    startDate: '',
    endDate: '',
    statusFilter: 'all'
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    tin: '',
    opening_balance: 0
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API_URL}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API_URL}/customers/${editingCustomer.id}`, formData);
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API_URL}/customers`, formData);
        toast.success('Customer created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      contact_person: customer.contact_person,
      phone: customer.phone,
      email: customer.email || '',
      address: customer.address,
      tin: customer.tin || '',
      opening_balance: customer.opening_balance || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    try {
      await axios.delete(`${API_URL}/customers/${id}`);
      toast.success('Customer deleted successfully');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      phone: '',
      email: '',
      address: '',
      tin: '',
      opening_balance: 0
    });
    setEditingCustomer(null);
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.includes(searchTerm)
  );

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 25;
  const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE) || 1;
  const safePage = Math.min(currentPage, totalPages);
  const paginatedCustomers = filteredCustomers.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const openStatementFilters = (customerId, customerName) => {
    setPreviewCustomerId(customerId);
    setPreviewCustomerName(customerName);
    setStatementFilters({ startDate: '', endDate: '', statusFilter: 'all' });
    setFilterDialogOpen(true);
  };

  const previewStatement = async (customerId, customerName, filters = {}) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.statusFilter && filters.statusFilter !== 'all') params.append('status_filter', filters.statusFilter);
      
      const queryString = params.toString();
      const url = `${API_URL}/reports/customers/${customerId}/statement${queryString ? `?${queryString}` : ''}`;
      
      toast.info('Generating statement...');
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Create blob URL with explicit PDF type
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(pdfBlob);
      
      // Download the file with Save As behavior
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `statement_${customerName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Close filter dialog and show success
      setFilterDialogOpen(false);
      toast.success(`Statement for ${customerName} downloaded`);
      
      // Revoke URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 5000);
      
    } catch (error) {
      toast.error('Failed to generate statement');
      console.error('Statement error:', error);
    }
  };

  const generateFilteredStatement = () => {
    previewStatement(previewCustomerId, previewCustomerName, statementFilters);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-semibold text-foreground">Customers</h1>
          <p className="text-muted-foreground">Manage your hotel and restaurant clients</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="add-customer-btn">
              <Plus className="w-5 h-5 mr-2" />
              Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="customer-dialog">
            <DialogHeader>
              <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Business Name</label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="customer-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Contact Person</label>
                <Input required value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} data-testid="customer-contact-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Phone</label>
                <Input required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="customer-phone-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="customer-email-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <Input required value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} data-testid="customer-address-input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">TIN (Tax Identification Number)</label>
                <Input 
                  value={formData.tin} 
                  onChange={(e) => setFormData({ ...formData, tin: e.target.value })} 
                  data-testid="customer-tin-input"
                  placeholder="e.g., 123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Opening Balance (RWF)</label>
                <Input 
                  type="number" 
                  step="0.01"
                  value={formData.opening_balance} 
                  onChange={(e) => setFormData({ ...formData, opening_balance: parseFloat(e.target.value) || 0 })} 
                  data-testid="customer-opening-balance-input"
                  placeholder="0.00"
                />
                <p className="text-xs text-muted-foreground mt-1">Amount owed by customer before using this system</p>
              </div>
              <Button type="submit" className="w-full" data-testid="customer-submit-btn">
                {editingCustomer ? 'Update Customer' : 'Create Customer'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white p-4 rounded-xl border border-border shadow-sm mb-4">
        <div className="flex items-center space-x-2">
          <Search className="w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search customers by name, contact, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1"
            data-testid="customer-search-input"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="customers-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Business Name</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Contact Person</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">Phone</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-foreground">TIN</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                    {searchTerm ? 'No customers match your search.' : 'No customers yet. Add your first customer to get started.'}
                  </td>
                </tr>
              )}
              {paginatedCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-muted/30 transition-colors" data-testid="customer-row">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">{customer.name}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{customer.contact_person}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{customer.phone}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{customer.tin || 'N/A'}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => openStatementFilters(customer.id, customer.name)} data-testid="preview-statement-btn" title="Generate Statement">
                        <FileText className="w-4 h-4 text-primary mr-1" />
                        Statement
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(customer)} data-testid="edit-customer-btn">
                        <Edit className="w-4 h-4" />
                      </Button>
                      {canDelete && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(customer.id)} data-testid="delete-customer-btn">
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={currentPage} pages={totalPages} total={filteredCustomers.length} limit={ITEMS_PER_PAGE} onPageChange={setCurrentPage} />
      </div>
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent className="max-w-md" data-testid="statement-filter-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Generate Statement - {previewCustomerName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Start Date</label>
                <Input
                  type="date"
                  value={statementFilters.startDate}
                  onChange={(e) => setStatementFilters({ ...statementFilters, startDate: e.target.value })}
                  data-testid="statement-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">End Date</label>
                <Input
                  type="date"
                  value={statementFilters.endDate}
                  onChange={(e) => setStatementFilters({ ...statementFilters, endDate: e.target.value })}
                  data-testid="statement-end-date"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Order Status Filter</label>
              <Select 
                value={statementFilters.statusFilter} 
                onValueChange={(value) => setStatementFilters({ ...statementFilters, statusFilter: value })}
              >
                <SelectTrigger data-testid="statement-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Orders</SelectItem>
                  <SelectItem value="pending">Orders with Balance Due</SelectItem>
                  <SelectItem value="unpaid">Unpaid Orders Only</SelectItem>
                  <SelectItem value="partial">Partially Paid Orders</SelectItem>
                  <SelectItem value="paid">Paid Orders Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => previewStatement(previewCustomerId, previewCustomerName, {})}
              >
                All Orders (No Filter)
              </Button>
              <Button 
                className="flex-1"
                onClick={generateFilteredStatement}
                data-testid="generate-statement-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Generate Statement
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;